import { html, LitElement, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { styleMap } from "lit/directives/style-map.js";

import { $page, ariaBoolean } from "..";
import css from "./bible-video-player-overlay.css?inline";

export type Video = {
  aspectRatio?: number;
  muxId: string;
  start?: number;
  title: string;
};

@customElement("bible-video-player-overlay")
export class VideoPlayerOverlayElement extends LitElement {
  static styles = unsafeCSS(css);

  private disconnectedCallbacks: (() => void)[] = [];

  @state()
  private ready = false;

  private refs = {
    close: createRef<HTMLButtonElement>(),
    root: createRef<HTMLElement>(),
    player: createRef<HTMLVideoElement>(),
  };

  @property()
  trigger: HTMLElement = null!;

  @property()
  video: Video = null!;

  connectedCallback() {
    super.connectedCallback();
    this.withCleanup(() => {
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          this.requestClose();
        }
        if (e.key === "Tab") {
          if (!this.refs.root.value?.contains(this.shadowRoot!.activeElement)) {
            this.refs.close.value?.focus();
          }
        }
      };
      $page.setAttribute("inert", "");
      document.body.addEventListener("keyup", handler);
      return () => {
        $page.removeAttribute("inert");
        document.body.removeEventListener("keyup", handler);
        this.trigger?.focus();
      };
    });
    customElements.whenDefined("mux-player").then(() => {
      const $player = this.refs.player.value!;
      // HACK: mux-player has a bug where it hides the controls
      // if the initial width is < 300, but will show them subsequently
      // if the player if resized to > 300
      $player.style.width = "300px";
      setTimeout(() => {
        $player.style.width = "";
        this.ready = true;
      }, 100);
    });
    // @ts-ignore
    import("https://cdn.jsdelivr.net/npm/@mux/mux-player");
  }

  disconnectedCallback() {
    while (this.disconnectedCallbacks.length > 0)
      this.disconnectedCallbacks.shift()!();
  }

  firstUpdated() {
    this.refs.close.value?.focus();
  }

  render() {
    const aspectRatio = this.video.aspectRatio ?? 16 / 9;
    return html`
      <div ${ref(this.refs.root)} class="overlay">
        <div
          class="overlay__content"
          data-orientation=${aspectRatio > 1 ? "landscape" : "portrait"}
          role="dialog"
          style=${styleMap({
            "--aspect-ratio": `${aspectRatio}`,
          })}
        >
          <div class="overlay__title">${this.video.title}</div>
          <button
            aria-title="Close"
            ${ref(this.refs.close)}
            @click=${this.requestClose}
          ></button>
          <mux-player
            @loadeddata=${() => {
              const $player = this.refs.player.value!;
              $player.currentTime = this.video.start ?? 0;
            }}
            ${ref(this.refs.player)}
            aria-hidden=${ariaBoolean(!this.ready)}
            default-hidden-captions
            playback-id=${this.video.muxId}
            playsinline
            stream-type="on-demand"
          ></mux-player>
        </div>
      </div>
    `;
  }

  private requestClose() {
    this.remove();
  }

  private withCleanup(fn: () => () => void) {
    this.disconnectedCallbacks.push(fn());
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bible-video-player-overlay": VideoPlayerOverlayElement;
  }
}
